import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent, type RefObject } from "react";
import type { BarArranging } from "../bar/arranging.ts";
import { SWAP_TARGET } from "../bar/page-follow.ts";
import { SwapHold, overSwapControl } from "../bar/swap-hold.ts";
import type { TipController } from "../bar/tips.ts";
import { DragGesture } from "../drag/gesture.ts";
import { placeGhost } from "../drag/ghost.tsx";
import { dropOf, dropTargetAt, dropsWhenReleased } from "../drag/targets.ts";
import type { DropTone } from "../../ui/kit/index.ts";
import type { BagSnapshot } from "./store.ts";

/**
 * Dragging in the Inventory window (P2.8-T14, ADR-091's note of that row; the gesture is ADR-121 decision 3.1's, which
 * P2.8-T29 builds the bar's drag on). A press on a slot that holds an item and waits for no answer becomes a drag once
 * it moves more than 8 CSS pixels inside 500 ms (`drag/gesture.ts`): the slot is picked up as a click would pick it
 * up, so the selection is the store's, and the item's ghost follows the pointer. Released over a slot of the bag it is
 * placed there (moved, merged or swapped once the server commits); released anywhere outside the Inventory window, the
 * chat and the hotbar (`dropsWhenReleased`), the stack is dropped onto the ground (P2.8-T34, ADR-091's amendment for
 * P2.8-T34, point 8); released elsewhere in those it is put back and nothing is sent. Over a slot of the bag the slot takes the positive ring and the ghost the positive marker where the move
 * would be sent, and the danger ring and marker where it would be put back (a merge onto a full stack, a slot waiting
 * for an answer), as the store's own reading says (`placeable`); no tooltip shows while a drag passes over slots. Escape, which puts the selection back, a lost window and a cancelled pointer end the drag the same way. A
 * touch never drags, so a long press keeps its tooltip, except a tool, which a long press then a drag carries to the tool
 * belt's chip (ADR-142 decision 3b). The click that follows a drop is swallowed, so a drop never
 * uses the item it lands on. Where the drop lands is read from what lies under the pointer (`drag/targets.ts`): the
 * bar's slots are targets too (P2.8-T29, ADR-121 decision 3.1), where an item that can stand on the bar replaces the
 * slot's thing and stays in the bag, the slot marked as the bar's arranging answers (`BarArranging.toneForItem`, the
 * lock asking for Shift onto an occupied slot); the bag's slot is put back either way. Where the bar pages, an item
 * held over the swap control for 500 ms shows the other page (P2.8-T30, `bar/swap-hold.ts`), the control drawn as a
 * slot that takes the drop meanwhile, and a cancelled drag turns the bar back to the page it started on.
 */

/**
 * What a drag asks of the bag: pick a slot up, whether it would be placed on another, place it there, put it back, and
 * drop a slot's stack onto the ground (P2.8-T34), absent where nothing drops. The bag store answers all five.
 */
export interface BagMoveActions {
  readonly pick: (slot: number) => boolean;
  readonly placeable: (slot: number) => boolean;
  readonly place: (slot: number) => boolean;
  readonly putBack: () => boolean;
  readonly drop?: ((slot: number) => boolean) | undefined;
}

/**
 * The slot of the bag a drag is held over and how it answers, or null over none.
 */
export interface DragOver {
  readonly index: number;
  readonly tone: DropTone;
}

/**
 * What the window draws a drag with: the press handler for its grid, the slot being dragged and the slot it is held
 * over (null for none), the ghost's element, and whether the click now arriving ends a drop and must be swallowed.
 */
export interface BagDrag {
  readonly onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  readonly dragging: number | null;
  readonly over: DragOver | null;
  readonly ghost: RefObject<HTMLDivElement | null>;
  readonly swallow: () => boolean;
  /**
   * Whether a touch press on a tool waits to become a drag to the tool belt's chip, whose long press must not also open
   * the slot's menu.
   */
  readonly carrying: () => boolean;
}

/**
 * Drags slots of the bag about.
 *
 * @param bag The bag as the window draws it.
 * @param actions The bag's pick, place and put back, or null where nothing moves.
 * @param tips The page's tooltip controller, whose tooltip a drag drops, or null.
 * @param bar The bar's arranging, whose slots take an item dropped on them, or null where the bar takes nothing.
 * @param drop Drops a slot released over the world, asking first where the window does (P2.8-T34); the bag's own drop
 *   when absent.
 * @returns What the window draws the drag with.
 */
export function useBagDrag(bag: BagSnapshot, actions: BagMoveActions | null, tips: Pick<TipController, "pressCancel" | "clear"> | null, bar: BarArranging | null = null, drop: ((slot: number) => boolean) | null = null): BagDrag {
  const gesture = useMemo(() => new DragGesture(), []);
  const ghost = useRef<HTMLDivElement>(null);
  const latest = useRef(bag);
  latest.current = bag;
  const dropping = useRef(drop);
  dropping.current = drop;
  const pointer = useRef({ x: 0, y: 0 });
  const swallowed = useRef(false);
  const lastOver = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<DragOver | null>(null);
  const hold = useMemo(() => (bar === null ? null : new SwapHold(() => bar.follow.turnForDrag())), [bar]);

  useEffect(() => {
    if (actions === null) {
      return undefined;
    }
    /**
     * Ends the drag's drawing.
     */
    const ended = (): void => {
      setDragging(null);
      setOver(null);
    };
    /**
     * Follows the pointer: starts the drag once the press has moved far enough in time, then moves the ghost and marks
     * the bag's slot under the pointer.
     *
     * @param event The move.
     */
    const moved = (event: globalThis.PointerEvent): void => {
      pointer.current.x = event.clientX;
      pointer.current.y = event.clientY;
      if (gesture.move(event.pointerId, event.clientX, event.clientY, performance.now())) {
        const source = gesture.active;
        if (source === null || !actions.pick(source.index)) {
          gesture.cancel();
          return;
        }
        tips?.pressCancel();
        tips?.clear();
        setDragging(source.index);
      }
      const source = gesture.active;
      if (source !== null) {
        placeGhost(ghost.current, event.clientX, event.clientY);
        const target = dropTargetAt(event.clientX, event.clientY);
        const item = latest.current.slots[source.index]?.item ?? null;
        if (bar !== null && item !== null) {
          const onSwap = overSwapControl(event.clientX, event.clientY);
          bar.hoverFromBag(onSwap ? SWAP_TARGET : target !== null && target.surface === "bar" ? target.index : null, item, event.shiftKey);
          hold?.over(onSwap);
        }
        bar?.belt?.setOver(item !== null && target !== null && target.surface === "belt" ? { index: target.index, tone: bar.belt.toneForItem(item) } : null);
        const index = target !== null && target.surface === "bag" && target.index !== source.index ? target.index : null;
        if (index !== lastOver.current) {
          lastOver.current = index;
          tips?.clear();
        }
        setOver((before) => {
          if (index === null) {
            return null;
          }
          const tone: DropTone = actions.placeable(index) ? "positive" : "danger";
          return before !== null && before.index === index && before.tone === tone ? before : { index, tone };
        });
      }
    };
    /**
     * Drops the drag where the pointer is released, and swallows the click that follows.
     *
     * @param event The release.
     */
    const released = (event: globalThis.PointerEvent): void => {
      const source = gesture.release(event.pointerId);
      if (source === null) {
        return;
      }
      hold?.end();
      const target = dropTargetAt(event.clientX, event.clientY);
      const item = latest.current.slots[source.index]?.item ?? null;
      const dropSlot = dropping.current ?? actions.drop;
      if (target !== null && target.surface === "bag") {
        actions.place(target.index);
      } else if (target !== null && target.surface === "bar" && bar !== null && item !== null) {
        bar.placeItem(target.index, item, event.shiftKey);
        actions.putBack();
      } else if (target !== null && target.surface === "belt" && bar?.belt != null && item !== null) {
        bar.belt.dropAt(target.index, item);
        actions.putBack();
      } else if (target === null && dropSlot !== undefined && dropsWhenReleased(event.clientX, event.clientY)) {
        if (!dropSlot(source.index)) {
          actions.putBack();
        }
      } else {
        actions.putBack();
      }
      bar?.endDrag();
      bar?.belt?.setOver(null);
      bar?.follow.dragLanded();
      swallowed.current = true;
      setTimeout(() => {
        swallowed.current = false;
      }, 0);
      ended();
    };
    /**
     * Ends a drag with nothing dropped, putting the slot back.
     */
    const cancelled = (): void => {
      hold?.end();
      if (gesture.cancel()) {
        actions.putBack();
        bar?.cancelDrag();
        bar?.belt?.setOver(null);
        ended();
      }
    };
    window.addEventListener("pointermove", moved);
    window.addEventListener("pointerup", released);
    window.addEventListener("pointercancel", cancelled);
    window.addEventListener("blur", cancelled);
    return () => {
      window.removeEventListener("pointermove", moved);
      window.removeEventListener("pointerup", released);
      window.removeEventListener("pointercancel", cancelled);
      window.removeEventListener("blur", cancelled);
      hold?.end();
      if (gesture.cancel()) {
        actions.putBack();
      }
    };
  }, [gesture, actions, tips, bar, hold]);

  useLayoutEffect(() => {
    if (dragging !== null) {
      placeGhost(ghost.current, pointer.current.x, pointer.current.y);
    }
  }, [dragging]);

  useEffect(() => {
    if (dragging !== null && bag.selected !== dragging) {
      gesture.cancel();
      hold?.end();
      bar?.cancelDrag();
      setDragging(null);
      setOver(null);
    }
  }, [bag.selected, dragging, gesture, hold, bar]);

  return useMemo(
    (): BagDrag => ({
      onPointerDown: (event) => {
        const held = latest.current;
        if (actions === null || event.button !== 0 || held.selected !== null) {
          return;
        }
        const anchor = dropOf(event.target as Element);
        const slot = anchor !== null && anchor.surface === "bag" ? held.slots[anchor.index] : undefined;
        if (anchor === null || slot === undefined || slot.item === null || slot.pending) {
          return;
        }
        const toBelt = event.pointerType === "touch" && bar?.belt?.isTool(slot.item) === true;
        if (event.pointerType === "touch" && !toBelt) {
          return;
        }
        pointer.current.x = event.clientX;
        pointer.current.y = event.clientY;
        gesture.press(anchor, event.pointerId, event.clientX, event.clientY, performance.now(), toBelt);
      },
      dragging,
      over,
      ghost,
      swallow: () => swallowed.current,
      carrying: () => gesture.waitingToHold,
    }),
    [actions, gesture, dragging, over],
  );
}
